// Pure helpers for the Export center: starter reports, plain wording for status and schedule, expiry bars, and "what runs next".
export const FORMAT_META = {
  pdf: { label: 'PDF', color: '#fca5a5' },
  csv: { label: 'CSV', color: '#4ade80' },
  sarif: { label: 'SARIF', color: '#93c5fd' },
  json: { label: 'JSON', color: '#fcd34d' }
};
const base = { scope: 'All findings', retention_days: 30, enabled: false, purge_after_expiry: true };
export const STARTERS = [
  { key: 'pdf', title: 'Report for management', text: 'A one-page summary of your security posture.', payload: { ...base, name: 'Report for management', format: 'pdf', options: { severity_filter: 'All severities' } } },
  { key: 'csv', title: 'Spreadsheet of findings', text: 'Every finding in a table you can sort and filter.', payload: { ...base, name: 'Spreadsheet of findings', format: 'csv', options: { severity_filter: 'All severities' } } },
  { key: 'sarif', title: 'File for your CI pipeline', text: 'Lets GitHub or GitLab show findings in code review.', payload: { ...base, name: 'Findings for CI', format: 'sarif', sarif_category: 'fuzzscan', options: { severity_filter: 'Critical + High' } } },
  { key: 'json', title: 'All the data', text: 'Everything, for your own tools and scripts.', payload: { ...base, name: 'All the data', format: 'json', options: { severity_filter: 'All severities' } } }
];
export const SCOPES = ['All findings', 'Web scans', 'Network scans', 'Reconnaissance', 'Mobile scans', 'Cloud scans'];
export const NETWORKS = [{ id: '', label: 'Both' }, { id: 'external', label: 'External only' }, { id: 'internal', label: 'Internal only' }];
export const SEVERITIES = ['All severities', 'Critical only', 'Critical + High', 'Medium and above'];
export const FREQUENCIES = [{ label: 'Every hour', value: 'hourly' }, { label: 'Every day', value: 'daily' }, { label: 'Every week', value: 'weekly' }, { label: 'Every month', value: 'monthly' }];
export const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const STATUS = {
  ready: { label: 'READY', color: '#22c55e' }, running: { label: 'RUNNING', color: '#38bdf8' }, failed: { label: 'FAILED', color: '#ef4444' },
  expired: { label: 'EXPIRED', color: '#64748b' }, waiting: { label: 'WAITING', color: '#f59e0b' }
};
export function statusOf(e) {
  if (e.status === 'queued' || e.status === 'processing') return 'running';
  if (e.status === 'expired') return 'expired';
  if (e.status === 'failed' || (e.last_status === 'failed' && !e.download_url)) return 'failed';
  if (e.download_url) return 'ready';
  return 'waiting';
}
export const needsAttention = (e) => statusOf(e) === 'failed' || e.last_status === 'failed';
export const isRunning = (e) => statusOf(e) === 'running';

const hhmm = (v) => String(v || '00:00:00').slice(0, 5);
export function scheduleText(e) {
  if (!e.enabled) return 'On demand';
  if (e.frequency === 'hourly') return `Every hour at :${hhmm(e.run_at).slice(3)}`;
  if (e.frequency === 'weekly') return `Every ${DAYS[Number(e.day_of_week) || 0]} at ${hhmm(e.run_at)}`;
  if (e.frequency === 'monthly') return `Every month at ${hhmm(e.run_at)}`;
  return `Every day at ${hhmm(e.run_at)}`;
}
export function scopeText(e) {
  const f = (e.options || {}).severity_filter;
  const n = (e.options || {}).network_scope;
  return `${e.scope || 'All findings'}${n ? ` · ${n} only` : ''}${f && f !== 'All severities' ? ` · ${f.toLowerCase()}` : ''}`;
}

// How long the file is kept: text for people and a 0-100 bar (full when new, empty when about to go).
export function expiry(e, now = new Date()) {
  if (statusOf(e) === 'expired') return { text: 'Deleted', pct: 0 };
  if (!e.expires_at) return { text: '', pct: 0 };
  const left = (new Date(e.expires_at) - now) / 86400000;
  const days = Math.ceil(left);
  const keep = Math.max(1, Number(e.retention_days) || 30);
  return { text: left <= 0 ? 'Deleted today' : days === 1 ? 'Deleted tomorrow' : `Deleted in ${days} days`, pct: Math.max(0, Math.min(100, Math.round((left / keep) * 100))) };
}

export const FILTERS = [
  { id: 'all', label: 'All', test: () => true },
  { id: 'scheduled', label: 'Scheduled', test: (e) => e.enabled },
  { id: 'attention', label: 'Needs attention', test: needsAttention }
];
export const filterExports = (list, id) => list.filter((FILTERS.find((f) => f.id === id) || FILTERS[0]).test);

const dayStart = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
// The next scheduled runs, soonest first, with a friendly day label.
export function nextRuns(list, now = new Date()) {
  return list.filter((e) => e.enabled && e.next_run_at).sort((a, b) => new Date(a.next_run_at) - new Date(b.next_run_at)).map((e) => {
    const at = new Date(e.next_run_at);
    const diff = Math.round((dayStart(at) - dayStart(now)) / 86400000);
    const soon = at - now < 3600000 && at > now;
    const day = soon ? `In ${Math.max(1, Math.round((at - now) / 60000))} min` : diff <= 0 ? 'Today' : diff === 1 ? 'Tomorrow' : at.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
    return { id: e.id, name: e.name, day, time: at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }), detail: `${(FORMAT_META[e.format] || { label: e.format }).label} · ${scheduleText(e).toLowerCase()}`, failed: e.last_status === 'failed' };
  });
}
export const cutShort = (e) => Boolean((e.options || {}).truncated);
